export enum SignalREventNames {
  NewAIChatActionOptions = 'newAIChatActionOptions',
  NewAIChatCancelled = 'newAIChatCancelled',
  NewAIChatProgress = 'newAIChatProgress',
  Interactive = 'interactive',
  // AI chatbot streaming events (naming differs across backends/environments)
  NewAIChatHtmlSnapshot = "newAIChatHtmlSnapshot",
  NewAIChatMessage = "newAIChatMessage",
  NewAIChatMessageMaya = "newAIChatMessageMaya",
  NewAIMessageSylvia = "newAIMessageSylvia",
  NewAIChatMessageSylvia = "newAIChatMessageSylvia",
  NewAIChatMessagePadre = "newAIChatMessagePadre",
  NewChatMessage = "newChatMessage",
  NextNotification = "newNotification",
}

export interface ChatCancellationAcknowledgement {
  version: 1;
  responseId: string;
  sessionId: string;
  agentId: string;
  legacyTarget: string;
}

export const normalizeChatCancellationAcknowledgement = (raw: unknown): ChatCancellationAcknowledgement | undefined => {
  try {
    const value = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!value || typeof value !== 'object' || Array.isArray(value) || value.version !== 1) return undefined;
    if (!['responseId', 'sessionId', 'agentId', 'legacyTarget'].every(key =>
      typeof value[key] === 'string' && value[key].trim().length > 0)) return undefined;
    return { version: 1, responseId: value.responseId, sessionId: value.sessionId,
      agentId: value.agentId, legacyTarget: value.legacyTarget };
  } catch {
    return undefined;
  }
};

export interface ChatActionOption {
  id: string;
  label: string;
}

/** Choices the backend offers when a message fits more than one action; labels are server-localized. */
export interface ChatActionOptions {
  version: 1;
  responseId: string;
  sessionId: string;
  agentId: string;
  legacyTarget: string;
  offerId: string;
  options: ChatActionOption[];
}

const MAX_CHAT_ACTION_OPTIONS = 4;
const MAX_CHAT_ACTION_LABEL_LENGTH = 200;

export const normalizeChatActionOptions = (raw: unknown): ChatActionOptions | undefined => {
  try {
    const value = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!value || typeof value !== 'object' || Array.isArray(value) || value.version !== 1) return undefined;
    if (!['responseId', 'sessionId', 'agentId', 'legacyTarget'].every(key =>
      typeof value[key] === 'string' && value[key].trim().length > 0)) return undefined;
    if (typeof value.offerId !== 'string' || !/^[0-9a-f]{32}$/.test(value.offerId)) return undefined;
    if (!Array.isArray(value.options) || value.options.length < 2
      || value.options.length > MAX_CHAT_ACTION_OPTIONS) return undefined;
    const options: ChatActionOption[] = [];
    for (const option of value.options) {
      if (!option || typeof option !== 'object' || typeof option.id !== 'string' || !/^o\d{1,2}$/.test(option.id)
        || typeof option.label !== 'string' || !option.label.trim()
        || option.label.length > MAX_CHAT_ACTION_LABEL_LENGTH
        || options.some(existing => existing.id === option.id)) return undefined;
      options.push({ id: option.id, label: option.label.trim() });
    }
    return { version: 1, responseId: value.responseId, sessionId: value.sessionId, agentId: value.agentId,
      legacyTarget: value.legacyTarget, offerId: value.offerId, options };
  } catch {
    return undefined;
  }
};

export interface ChatProgress {
  version: 1;
  responseId: string;
  sessionId: string;
  agentId: string;
  legacyTarget: string;
  sequence: number;
  sourceHost?: string;
  sourceTitle?: string;
  /** Opaque per-source key correlating source markers with a later `source_accepted` marker. */
  sourceKey?: string;
  citationsFound?: number;
  citationsRetained?: number;
  stage: 'working' | 'searching' | 'tool_running' | 'tool_returned'
    | 'searching_documents' | 'searching_web' | 'searching_documents_web' | 'reviewing_sources'
    | 'reading_documents' | 'reading_web_sources' | 'checking_source_safety' | 'assessing_sources' | 'combining_sources'
    | 'summarizing_document' | 'summarizing_discussion' | 'reviewing_health'
    | 'updating_symptoms' | 'updating_medications' | 'updating_treatments' | 'updating_diagnoses'
    | 'updating_journal' | 'updating_health' | 'preparing_email' | 'checking_members'
    | 'creating_community' | 'updating_community' | 'removing_community' | 'updating_members'
    | 'updating_permissions' | 'checking_notifications' | 'checking_profile' | 'updating_profile'
    | 'updating_credentials' | 'updating_journey' | 'updating_preferences' | 'checking_onboarding'
    | 'updating_onboarding' | 'preparing_survey' | 'recording_survey' | 'reviewing_needs'
    | 'updating_needs' | 'finding_sites' | 'preparing_response'
    | 'composing' | 'validating' | 'retrying' | 'source_accepted' | 'finished';
}

export type ChatProgressHistoryEntry = ChatProgress & { sourceAccepted?: boolean };

export interface ChatHtmlSnapshot {
  version: number;
  responseId: string;
  sessionId: string;
  agentId: string | null;
  legacyTarget: string;
  segmentId: number;
  sequence: number;
  html: string;
  isSegmentFinal: boolean;
}

export type ServerDirectiveAction =
  | 'resumeOnboarding'
  | 'resumeonboarding'
  | 'onboardingComplete'
  | 'onboardingcomplete'
  | 'onboardingStepComplete'
  | 'onboardingstepcomplete'
  | 'navigate'
  | 'refetchData'
  | 'refetchdata'
  | 'myCareUpdated'
  | 'mycareupdated'
  | 'showMessage'
  | 'showmessage'
  | 'activeSubject'
  | 'activesubject'
  | 'onboardingSectionActive'
  | 'onboardingsectionactive'
  | 'onboardingOverview'
  | 'onboardingoverview';

export type ServerDirectivePriority = 'silent' | 'normal' | 'urgent';

// ── MyCare per-item change directive payload (under params) ──

export type MyCareItemType = 'symptom' | 'medication' | 'treatment' | 'diagnosis';

export interface MyCareChangeDetail {
  action: 'added' | 'updated';
  itemType: MyCareItemType;
  itemId: string | null;
  itemDisplayName: string;
  occurredAt: string;           // ISO-8601 UTC
}

export interface MyCareChangeParams {
  subjectMemberId: string;
  subjectJourneyId: string;
  subjectDisplayName: string | null;
  subjectRelationship: string | null;

  speakerMemberId: string;
  speakerIsSubject: boolean;

  change: MyCareChangeDetail;

  /** Legacy back-compat */
  created: number;
  enriched: number;
}

/** Type guard for new per-item myCareUpdated directives. */
export const isMyCareChangeDirective = (
  params: Record<string, unknown> | undefined
): params is Record<string, unknown> & MyCareChangeParams =>
  Boolean(params && typeof params === 'object' && 'change' in params);

export interface ServerDirective {
  type: 'serverDirective';
  action: ServerDirectiveAction;
  target?: string;
  params?: Record<string, unknown>;
  reason?: string;
  priority?: ServerDirectivePriority;
}

export interface OnboardingChatContext {
  memberId: string;
  roomId: string;
  sessionId: string;
  agentId: string;
}

export const parseOnboardingChatContext = (value: unknown): OnboardingChatContext | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  const context = value as Record<string, unknown>;
  if (context.version !== 1 ||
      typeof context.memberId !== 'string' || !context.memberId.trim() ||
      typeof context.roomId !== 'string' || !context.roomId.trim() ||
      typeof context.sessionId !== 'string' || !context.sessionId.trim() ||
      typeof context.agentId !== 'string' || !context.agentId.trim()) return undefined;
  return { memberId: context.memberId, roomId: context.roomId, sessionId: context.sessionId, agentId: context.agentId };
};

/**
 * Type guard: returns true when a newNotification payload is a ServerDirective.
 * Handles both camelCase (JS convention) and PascalCase (.NET serialisation).
 */
export const isServerDirective = (payload: unknown): payload is ServerDirective => {
  if (typeof payload !== 'object' || payload === null) return false;
  const p = payload as Record<string, unknown>;
  const typeVal = p.type ?? p.Type;
  return (
    (typeVal === 'serverDirective' || typeVal === 'ServerDirective') &&
    typeof (p.action ?? p.Action) === 'string'
  );
};

/**
 * Normalises a raw SignalR payload from PascalCase (.NET) to camelCase.
 * Safe to call on already-camelCase payloads.
 */
export const normalizeServerDirective = (raw: Record<string, unknown>): ServerDirective => ({
  type: 'serverDirective',
  action: ((raw.action ?? raw.Action) as string).toLowerCase() as ServerDirectiveAction,
  target: (raw.target ?? raw.Target) as string | undefined,
  params: (raw.params ?? raw.Params) as Record<string, unknown> | undefined,
  reason: (raw.reason ?? raw.Reason) as string | undefined,
  priority: ((raw.priority ?? raw.Priority) as ServerDirectivePriority | undefined) ?? 'normal',
});